import React from 'react';
import moment from 'moment-jalaali';
import { ITags } from 'store/providers/providerTypes';
import { IUsers } from 'store/store';
import {
  IAnswersView,
  IChoiceView,
  IGridAnswerView,
  IGridQuestionView,
  IMultipleChoiceAnswerView,
  IMultipleChoiceQuestionView,
  ISubAnswer,
  ISurveyItem,
  ISurveyResponseView,
  ISurveyViewServer,
  ISurveyViewWithData,
  QuestionType,
} from 'store/survey/surveyTypes';
import {
  englishNumber,
  isANumber,
  convertDigitToProperFormat,
} from 'utils/number';
import { smartNumericCompare } from 'utils/string';
import Icon from 'components/Icon';
import Input from 'components/Input';
import RelatedResponseItem from './RelatedResponseItem';
import { getEffectiveName } from 'components/FileManager/ItemsViewer/Survey/SurveyEditor/UniqueQuestion/PeriodTypeSelector/DatePeriod';
import { RelatedItemsWrapper, PrintIcon, PrintSection } from './styles';
import { getDistinctiveAnswerAsString } from '../../DistinctiveAnswers/DistinctiveAnswers';
import i18n from '../../../i18n';

interface IComponentProps {
  tags: ITags['data'];
  title: string;
  users: IUsers['map'];
  surveys: ISurveyViewServer[];
  responses: Array<ISurveyResponseView>;
  selectedTags: number[];
  selectedChoices: Array<string | number>;
  local: string;
  survey?: ISurveyViewWithData;
  surveyItem?: ISurveyItem;
  providerId?: number | undefined;
  isInDashboardView: boolean;
}

type IRelatedQuestion = IMultipleChoiceQuestionView | IGridQuestionView;

interface IComponentStates {
  singlePagePrint: boolean;
  splitPerDistrict: boolean;
}

const areChoiceValuesEqual = (
  selectedValue: string | number,
  answerValue: string | number | undefined
) => {
  if (answerValue === undefined || answerValue === null) return false;
  return String(selectedValue) === String(answerValue);
};

async function addCommentsFromColumn(worksheet: any, columnIndex: number) {
  // Get the range of the worksheet

  const XLSX = await import('xlsx');
  const range = XLSX.utils.decode_range(worksheet['!ref']!);

  // Iterate over column A
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellA = worksheet[XLSX.utils.encode_cell({ r: row, c: columnIndex })]; // Column A
    const cellB =
      worksheet[XLSX.utils.encode_cell({ r: row, c: columnIndex - 1 })]; // Column B

    if (cellA && cellB && cellA.v) {
      cellB.c = [{ a: 'توضیح', t: cellA.v }];
      cellB.c.hidden = true; // Add comment to cell B
    }
  }
}

async function findColumnIndexByValue(worksheet: any, targetValue: string) {
  const XLSX = await import('xlsx');
  const range = XLSX.utils.decode_range(worksheet['!ref']!);
  const firstRow = range.s.r;

  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellAddress = XLSX.utils.encode_cell({ r: firstRow, c: col });
    const cell = worksheet[cellAddress];
    if (cell && cell.v === targetValue) {
      return col;
    }
  }

  return -1; // Not found
}

async function mergeVerticalCells(worksheet: any, columns: string[]) {
  // Convert worksheet data to a 2D array
  const XLSX = await import('xlsx');
  const data = XLSX.utils.sheet_to_json(worksheet);

  // Function to identify and create merge ranges
  function findVerticalMerges(data: any, column: string) {
    const merges = [];
    const colIndex = findColumnIndexByValue(worksheet, column);

    for (let i = 0; i < data.length; i++) {
      const currentValue = data[i][column];
      const startRow = i;

      while (i + 1 < data.length && data[i + 1][column] === currentValue) {
        i++;
      }

      if (i !== startRow) {
        merges.push({
          s: { r: startRow + 1, c: colIndex },
          e: { r: i + 1, c: colIndex },
        });
      }
    }

    return merges;
  }

  const mergeRanges: any[] = [];
  columns.forEach((c) => mergeRanges.push(...findVerticalMerges(data, c)));
  worksheet['!merges'] = mergeRanges;
}

class RelatedResponsesList extends React.PureComponent<
  IComponentProps,
  IComponentStates
> {
  state: IComponentStates = { singlePagePrint: true, splitPerDistrict: true };
  local = this.props.local;

  handleExportExcel = async () => {
    const XLSX = await import('xlsx');
    const useAvg = !this.state.splitPerDistrict;
    const workbook = XLSX.utils.book_new();
    const result: any[] = [];
    const { tags, surveys, responses, selectedTags, selectedChoices } =
      this.props;

    const foundDistricts = new Set<string>();
    const foundPeriods = new Set<string>();
    const foundParentTags = new Set<string>();

    Object.values(responses || {})
      .sort((a, b) => {
        const relatedSurveyA = surveys.find(
          (survey: ISurveyViewServer) => survey.id === a.surveyId
        );
        const relatedSurveyB = surveys.find(
          (survey: ISurveyViewServer) => survey.id === b.surveyId
        );
        return smartNumericCompare(
          (relatedSurveyA as ISurveyViewServer).name,
          (relatedSurveyB as ISurveyViewServer).name
        );
      })
      .forEach((response, responseIndex) => {
        const relatedSurvey = surveys.find(
          (survey: ISurveyViewServer) => survey.id === response.surveyId
        )!;
        const periodType = relatedSurvey.periodType;
        const questionList =
          relatedSurvey.id === this.props.survey?.data.id //use viewing version if specific survey selected
            ? this.props.survey.data.versions[this.props.survey.viewingVersion!]
                .questionList || relatedSurvey.surveyQuestionList
            : relatedSurvey.surveyQuestionList;
        const distinctiveQuestions: IMultipleChoiceQuestionView[] = questionList
          .filter(
            (question) =>
              question.type === 'multipleChoice' &&
              !question.deleted &&
              question.distinctive
          )
          .map((it) => it as IMultipleChoiceQuestionView);

        const hasSelectedTags = selectedTags.length > 0;
        const hasSelectedChoices = selectedChoices.length > 0;
        const relatedQuestionsList = questionList as IRelatedQuestion[];
        // filter questions by tags
        const filteredRelatedQuestionsList = hasSelectedTags
          ? relatedQuestionsList.filter((question) => {
              return (
                question.tags &&
                selectedTags.every((selectedTagId) =>
                  question.tags.some(
                    (questionTag) => selectedTagId === questionTag.id
                  )
                )
              );
            })
          : relatedQuestionsList;

        // filter answers by choices
        const answersList = response.surveyAnswerList.filter(
          (answer: IAnswersView) => {
            if (answer.type === 'grid' || answer.type === 'multipleChoice') {
              return true;
            }
            return false;
          }
        );
        const filteredAnswersList = (
          hasSelectedChoices
            ? Object.values(answersList || {}).filter((answer) => {
                if (answer.type === QuestionType.MultipleChoice) {
                  const selectedList = Array.isArray(
                    (answer as IMultipleChoiceAnswerView).selected
                  )
                    ? (answer as IMultipleChoiceAnswerView).selected
                    : [];
                  if (typeof answer.value === 'string' && answer.value !== '') {
                    return selectedChoices.some((choice) =>
                      areChoiceValuesEqual(choice, answer.value)
                    );
                  } else if (
                    (answer as IMultipleChoiceAnswerView).values &&
                    (answer as IMultipleChoiceAnswerView).values.length > 0
                  ) {
                    return (answer as IMultipleChoiceAnswerView).values.find(
                      (choiceValue: string) =>
                        selectedChoices.some((choice) =>
                          areChoiceValuesEqual(choice, choiceValue)
                        )
                    );
                  } else {
                    return selectedList.find((tagId: number) =>
                      selectedChoices.some((choice) =>
                        areChoiceValuesEqual(choice, tagId)
                      )
                    );
                  }
                } else {
                  return (answer as IGridAnswerView).subAnswers.find(
                    (subAnswer: ISubAnswer) =>
                      selectedChoices.some((choice) =>
                        areChoiceValuesEqual(choice, subAnswer.value)
                      )
                  );
                }
              })
            : answersList
        ).filter((answer) => {
          return (
            !!answer &&
            !!filteredRelatedQuestionsList.find(
              (question: IRelatedQuestion) => question.id === answer.questionId
            )
          );
        });

        if (
          filteredRelatedQuestionsList.length !== 0 &&
          filteredAnswersList[0]
        ) {
          filteredAnswersList
            .sort((a, b) => {
              // sort answers by their question's order
              const aQuestion = filteredRelatedQuestionsList.find(
                (q) => q.id === a.questionId
              );
              const bQuestion = filteredRelatedQuestionsList.find(
                (q) => q.id === b.questionId
              );
              if (aQuestion && bQuestion) {
                return aQuestion.order > bQuestion.order ? 1 : -1;
              }
              return 0;
            })
            .forEach((answer, qIndex) => {
              const relatedQuestion = filteredRelatedQuestionsList.find(
                (question: IRelatedQuestion) =>
                  question.id === answer.questionId
              );
              if (!relatedQuestion || relatedQuestion.distinctive) return;

              const item = {
                surveyName: '\u200F' + relatedSurvey?.name,
                questionName: '\u200F' + relatedQuestion?.title,
                subQuestionName: '',
                distinctiveAnswers: getDistinctiveAnswerAsString(
                  tags,
                  answersList,
                  distinctiveQuestions
                ),
                relatedDate:
                  periodType !== 'none' &&
                  getEffectiveName(
                    periodType,
                    Number(response.effectiveDate),
                    this.props.local
                  ),
                // response: '',
                responseValue: 0 as number | null,
                ...relatedQuestion.tags?.reduce<Record<string, string>>(
                  (current, item) => {
                    const currentTag = this.props.tags[Number(item.id)];
                    if (!currentTag) return current;
                    const parentTagId = currentTag.parentId;
                    const tagName = currentTag.text;
                    if (
                      [1, 2, 3, 4, 5, 6, 7, 8, 9].find((it) =>
                        englishNumber(tagName).includes(it.toString())
                      )
                    )
                      return current;
                    if (!parentTagId) return current;
                    const parentTag = this.props.tags[Number(parentTagId)];
                    if (!parentTag) return current;
                    const parentTagName = parentTag.text;
                    current[parentTagName] =
                      (current[parentTagName] || '') +
                      (current[parentTagName] ? ', ' : '') +
                      tagName;
                    foundParentTags.add(parentTagName);
                    return current;
                  },
                  {}
                ),
                order: responseIndex! * 1000 + qIndex!,
              };
              foundPeriods.add(item.relatedDate || 'پاسخ');
              if (useAvg) item.distinctiveAnswers = '';
              foundDistricts.add(item.distinctiveAnswers);
              const hasFilter = selectedChoices.length > 0;
              if (relatedQuestion.type === 'grid') {
                const subAnswers: any = hasFilter
                  ? (answer as IGridAnswerView).subAnswers.filter(
                      (subAnswer: ISubAnswer) =>
                        selectedChoices.includes(subAnswer.value)
                    )
                  : (answer as IGridAnswerView).subAnswers;
                subAnswers
                  // .valueSeq()
                  .sort((a: ISubAnswer, b: ISubAnswer) => {
                    // sort by grid question part's indexes
                    const gridQuestionParts = (
                      relatedQuestion as IGridQuestionView
                    ).parts;
                    const aAnswerIndex = gridQuestionParts.findIndex(
                      (part) => part.value === a.subQuestionValue
                    );
                    const bAnswerIndex = gridQuestionParts.findIndex(
                      (part) => part.value === b.subQuestionValue
                    );
                    return aAnswerIndex > bAnswerIndex ? 1 : -1;
                  })
                  .forEach((subAnswer: ISubAnswer, subIndex: number) => {
                    const subQuestion = (
                      relatedQuestion as IGridQuestionView
                    ).parts.find(
                      (part: IChoiceView) =>
                        part.value === subAnswer.subQuestionValue
                    );
                    const subValue = relatedQuestion.choiceList.find(
                      (choice: IChoiceView) => choice.value === subAnswer.value
                    );
                    const res = { ...item };
                    res.order += subIndex / 1000;
                    res.subQuestionName = '\u200F' + subQuestion!.text;
                    res.responseValue = subValue!.score;
                    // res.response = subValue!.text;
                    // res.description = subAnswer.description;
                    result.push(res);
                  });
              } else if (relatedQuestion.type === 'multipleChoice') {
                const res = { ...item };
                res.subQuestionName = '-';
                // res.description = answer.description;
                if (typeof answer.value === 'string' && answer.value !== '') {
                  const choice = relatedQuestion.choiceList.find(
                    (item: IChoiceView) =>
                      item.value === answer.value || relatedQuestion.distinctive
                  );
                  res.responseValue = choice?.score || null;
                  // res.response = choice?.text || '';
                } else if (
                  (answer as IMultipleChoiceAnswerView).values &&
                  (answer as IMultipleChoiceAnswerView).values.length > 0
                ) {
                  let response = '';
                  let valueSum = 0;

                  Object.values(
                    (answer as IMultipleChoiceAnswerView).values || {}
                  ).forEach((value: string) => {
                    const choice = relatedQuestion.choiceList.find(
                      (item) => item.value === value
                    );
                    if (choice) {
                      response += (response.length ? ', ' : '') + choice.text;
                      if (choice.score) valueSum += choice.score;
                    }
                  });
                  res.responseValue = valueSum;
                  // res.response = response;
                }
                result.push(res);
              }
            });
        }
      });

    foundDistricts.forEach((district) => {
      let resultInDistrict = result.filter(
        (it) => it.distinctiveAnswers === district
      );

      resultInDistrict = resultInDistrict.reduce((current, item) => {
        let found = current.find(
          (it: any) =>
            it.surveyName === item.surveyName &&
            it.questionName === item.questionName &&
            it.subQuestionName === item.subQuestionName
        );
        if (!found) {
          found = item;
          current.push(item);
        }
        if (!found[item.relatedDate + 'count']) {
          found[item.relatedDate + 'count'] = 0;
          found[item.relatedDate] = 0;
        }
        if (isANumber(item.responseValue)) {
          found[item.relatedDate] =
            (found[item.relatedDate + 'count'] * found[item.relatedDate] +
              item.responseValue) /
            (found[item.relatedDate + 'count'] + 1);
          found[item.relatedDate + 'count']++;
        }
        if (item.description)
          found[item.relatedDate + '-توضیح'] =
            (!found[item.relatedDate + '-توضیح']
              ? ''
              : found[item.relatedDate + '-توضیح'] + ' / ') + item.description;
        delete found.responseValue;
        delete found.order;
        delete found.relatedDate;
        delete found.distinctiveAnswers;
        return current;
      }, []);
      resultInDistrict = resultInDistrict.sort((a, b) => a.order - b.order);
      resultInDistrict.forEach((r) => {
        foundPeriods.forEach((p) => {
          delete r[p + 'count'];
        });
      });
      const worksheet = XLSX.utils.json_to_sheet(resultInDistrict);
      const dataLength = {} as any;
      resultInDistrict.forEach((r) => {
        Object.entries(r).forEach(([k, v]) => {
          const name = k;
          const len = (v as string | number | null)?.toString()?.length ?? 10;
          if (!dataLength[name] || dataLength[name] < len) {
            dataLength[k] = Math.max(10, len);
          }
        });
      });
      const columnNames = [
        i18n.t('standard', { ns: 'survey' }),
        i18n.t('indicator', { ns: 'survey' }),
        i18n.t('part', { ns: 'survey' }),
        // messages.distinctiveAnswers,
        // messages.tags,
        // messages.response,
        // messages.responseValue,
        ...Array.from(foundParentTags),
        ...Array.from(foundPeriods),
        // messages.senderName,
        // messages.submitDate,
        // messages.description,
      ];
      XLSX.utils.sheet_add_aoa(worksheet, [columnNames], { origin: 'A1' });

      worksheet['!cols'] = [
        { wch: dataLength.surveyName },
        { wch: dataLength.questionName },
        { wch: dataLength.subQuestionName },
        // { wch: dataLength.distinctiveAnswers },
        // { wch: dataLength.relatedDate },
        ...Array.from(foundParentTags).map((it) => {
          return { wch: dataLength[it] };
        }), // { wch: dataLength.response },
        // { wch: dataLength.responseValue },
        ...Array.from(foundPeriods).reduce((current, it) => {
          current.push({ wch: dataLength[it] });
          current.push({ wch: dataLength[it + '-توضیح'] || 10 });
          return current;
        }, [] as any[]),
        // { wch: dataLength.senderName },
        // { wch: dataLength.submitDate },
        // { wch: dataLength.description },
      ];
      mergeVerticalCells(worksheet, [
        i18n.t('standard', { ns: 'survey' }),
        i18n.t('indicator', { ns: 'survey' }),
      ]);
      Array.from(foundPeriods).forEach((_, index) => {
        const src = 2 * index + 3 + foundParentTags.size + 1;
        addCommentsFromColumn(worksheet, src).then();
        worksheet['!cols']![src].hidden = true;
      });
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        (district || i18n.t('entireHospital', { ns: 'survey' })).substring(
          0,
          31
        )
      );
    });

    workbook.Workbook = workbook.Workbook || {};
    workbook.Workbook.Views = workbook.Workbook.Views || [];

    // Set the RTL property for the first view
    workbook.Workbook.Views[0] = workbook.Workbook.Views[0] || {};
    workbook.Workbook.Views[0].RTL = true;

    XLSX.writeFile(
      workbook,
      (this.props.title || i18n.t('responses', { ns: 'survey' })) + '.xlsx'
    );
  };
  handlePrintAll = () => {
    const data = {
      tags: this.props.tags,
      users: this.props.users,
      surveys: this.props.surveys,
      responses: this.props.responses,
      selectedTags: this.props.selectedTags ? this.props.selectedTags : [],
      singlePagePrint: this.state.singlePagePrint,
      selectedChoices: this.props.selectedChoices,
      survey: this.props.survey,
      surveyItem: this.props.surveyItem,
      providerId: this.props.providerId,
    };
    localStorage.setItem('printableResponses', JSON.stringify(data));
    const win = window.open('/print/responses', '_blank');
    if (win) {
      win.focus();
    }
  };

  handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name;
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    if (name === 'singlePagePrint') {
      this.setState({ singlePagePrint: value as boolean });
    }
    if (name === 'splitPerDistrict') {
      this.setState({ splitPerDistrict: value as boolean });
    }
  };

  render() {
    const { tags, users, surveys, responses, selectedTags, selectedChoices } =
      this.props;
    return (
      <RelatedItemsWrapper>
        {responses.length === 0 ? (
          <span style={{ textAlign: 'center' }}>
            {i18n.t('itemNotFound', { ns: 'survey' }) as string}
          </span>
        ) : (
          <React.Fragment>
            {!this.props.isInDashboardView && (
              <div style={{ display: 'flex', gap: '10px ' }}>
                <PrintSection>
                  <PrintIcon onClick={this.handlePrintAll}>
                    <Icon type="printer" />
                    <span>{i18n.t('print', { ns: 'survey' }) as string}</span>
                  </PrintIcon>
                  <Input
                    id="singlePagePrint"
                    name="singlePagePrint"
                    type="checkbox"
                    label={
                      i18n.t('printEachItemInAPage', { ns: 'survey' }) as string
                    }
                    checked={this.state.singlePagePrint}
                    onChange={this.handleChangeInput}
                  />
                </PrintSection>
                <PrintSection>
                  <PrintIcon onClick={this.handleExportExcel}>
                    <Icon type="grid" />
                    <span>
                      {
                        i18n.t('exelExportForAccredit', {
                          ns: 'survey',
                        }) as string
                      }
                    </span>
                  </PrintIcon>
                  <Input
                    id="splitPerDistrict"
                    name="splitPerDistrict"
                    type="checkbox"
                    label={
                      i18n.t('splitPerDistrict', { ns: 'survey' }) as string
                    }
                    checked={this.state.splitPerDistrict}
                    onChange={this.handleChangeInput}
                  />
                </PrintSection>
              </div>
            )}
            {Object.values(responses || {})
              .sort((a, b) => {
                // sort responses by effective/submitDate or surveyId
                const aDate = a.effectiveDate || a.submitDate || 0;
                const bDate = b.effectiveDate || b.submitDate || 0;
                if (aDate === bDate) {
                  return a.surveyId - b.surveyId;
                } else {
                  return aDate - bDate;
                }
              })
              .map((response: ISurveyResponseView) => {
                const responseId = response.id;
                const relatedSurvey = surveys.find(
                  (survey: ISurveyViewServer) => survey.id === response.surveyId
                );
                const periodType = (relatedSurvey as ISurveyViewServer)
                  .periodType;
                const questionList =
                  relatedSurvey?.id === this.props.survey?.data.id // use viewing version if specific survey selected
                    ? this.props.survey?.data.versions[
                        this.props.survey.viewingVersion!
                      ]?.questionList || relatedSurvey?.surveyQuestionList
                    : relatedSurvey?.surveyQuestionList;
                const distinctiveQuestions =
                  questionList?.filter(
                    (question) =>
                      question.type === 'multipleChoice' &&
                      !question.deleted &&
                      question.distinctive
                  ) || [];

                return (
                  <RelatedResponseItem
                    key={`${responseId}_${response.surveyId}`}
                    survey={this.props.survey?.data}
                    surveyItem={this.props.surveyItem}
                    tags={tags}
                    title={response.title || ''}
                    answeredBy={users[response.userId!]?.name}
                    selectedTags={selectedTags}
                    selectedChoices={selectedChoices}
                    submitDate={convertDigitToProperFormat(
                      moment(response.submitDate).format('jD jMMMM jYYYY')
                    )}
                    relatedQuestionsList={questionList as IRelatedQuestion[]}
                    distinctiveQuestions={
                      distinctiveQuestions as IMultipleChoiceQuestionView[]
                    }
                    effectiveDate={
                      periodType !== 'none' &&
                      getEffectiveName(
                        periodType,
                        Number(response.effectiveDate),
                        this.props.local
                      )
                    }
                    answersList={response.surveyAnswerList}
                    providerId={this.props.providerId}
                  />
                );
              })}
          </React.Fragment>
        )}
      </RelatedItemsWrapper>
    );
  }
}

export default RelatedResponsesList;
